import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { t, STRINGS } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SMS_CONSENT_VERSION } from "@/lib/consent";
import SmsConsent from "@/components/gentry/SmsConsent";

export default function LeadForm({ open, onOpenChange, lang, sessionId, initialReason }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState(initialReason || "");
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setPhone("");
      setEmail("");
      setReason("");
      setSmsConsent(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !reason.trim()) {
      toast.error(lang === "ht" ? "Tanpri ranpli tout chan yo." : "Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        session_id: sessionId,
        name,
        phone,
        email: email || null,
        reason,
        language: lang,
        sms_consent: smsConsent,
        sms_consent_text: smsConsent ? STRINGS[lang].smsConsentBody : null,
        sms_consent_version: smsConsent ? SMS_CONSENT_VERSION : null,
      });
      toast.success(t(lang, "successMessage"));
      onOpenChange(false);
      setName("");
      setPhone("");
      setReason("");
      setSmsConsent(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#1E3B2F]">
            {t(lang, "leaveMessage")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <label className="font-label text-[10px] text-[#6B7280]">{t(lang, "yourName")}</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-11 bg-[#F9F6F0] border-[#EBE4D3]"
              data-testid="lead-form-name-input"
              placeholder={lang === "ht" ? "Non ou" : "Jane Smith"}
            />
          </div>
          <div className="space-y-2">
            <label className="font-label text-[10px] text-[#6B7280]">{t(lang, "phone")}</label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-11 bg-[#F9F6F0] border-[#EBE4D3]"
              data-testid="lead-form-phone-input"
              placeholder="(713) 555-0142"
              type="tel"
            />
          </div>
          <div className="space-y-2">
            <label className="font-label text-[10px] text-[#6B7280]">{t(lang, "email")}</label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 bg-[#F9F6F0] border-[#EBE4D3]"
              data-testid="lead-form-email-input"
              placeholder={t(lang, "emailPlaceholder")}
              type="email"
              autoComplete="email"
            />
          </div>
          <div className="space-y-2">
            <label className="font-label text-[10px] text-[#6B7280]">{t(lang, "reason")}</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-24 bg-[#F9F6F0] border-[#EBE4D3]"
              data-testid="lead-form-reason-input"
              placeholder={
                lang === "ht"
                  ? "Mwen ta renmen konnen plis sou..."
                  : "I'd like to know more about..."
              }
            />
          </div>
          <SmsConsent checked={smsConsent} onCheckedChange={setSmsConsent} lang={lang} />
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-[#C25934] hover:bg-[#a44827] text-white btn-lift"
            data-testid="lead-form-submit-button"
          >
            {smsConsent ? t(lang, "submitBtnSignUp") : t(lang, "submitBtnSend")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
